import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/common/loader';
import { useExternalAuthChecks } from '../auth/useExternalAuthChecks';
import { runExternalAuthPipeline } from '../auth/externalAuthPipeline';

const AuthSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant, isAddEditHost, initializeTenant } = useTenant();
  const { session } = useAuth();
  const externalAuthChecks = useExternalAuthChecks();
  const [, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleAuthSuccess = async () => {
      try {
        // For addedit hosts, ensure we have the correct tenant from session
        if (isAddEditHost() && session?.user?.tenant) {
          await initializeTenant();
        }

        // Run all enabled external auth checks (RESO, Promote, etc.)
        await runExternalAuthPipeline(externalAuthChecks, {
          onCheckStart: () => {},
          log: (source, message, level) => {
            if (level === 'error') {
              console.error(`[${source}] ${message}`);
            }
          },
        });

        // Extract the next parameter with a more reliable method
        const fullSearch = location.search;

        // Remove the leading '?' if present
        const searchWithoutQuestion = fullSearch.startsWith('?')
          ? fullSearch.substring(1)
          : fullSearch;

        // Split by 'next=' to get everything after it
        const parts = searchWithoutQuestion.split('next=');

        if (parts.length > 1) {
          // This is everything after 'next=' - the complete URL
          const encodedNext = parts[1];
          const next = decodeURIComponent(encodedNext);

          try {
            // Extract just the path and query part from the full URL
            const urlObj = new URL(next);
            let targetPath = urlObj.pathname + urlObj.search;

            // For addedit hosts, ensure the path includes the tenant
            if (isAddEditHost() && session?.user?.tenant) {
              const tenantPrefix = `/${session.user.tenant}`;

              // If the target path doesn't start with the tenant, add it
              if (!targetPath.startsWith(tenantPrefix)) {
                // Remove leading slash from targetPath if present
                const pathWithoutLeadingSlash = targetPath.startsWith('/')
                  ? targetPath.substring(1)
                  : targetPath;

                // Construct the correct path
                targetPath = `${tenantPrefix}/${pathWithoutLeadingSlash}`;
              }
            }

            // Preview routes live in a separate Router tree (PreviewGate).
            // A client-side navigate can't reach them from the auth branch,
            // so force a full reload so isPreviewPath() re-evaluates.
            if (targetPath.includes('/preview/')) {
              window.location.href = targetPath;
              return;
            }

            navigate(targetPath);
          } catch (e) {
            console.error('Invalid next URL:', next, e);

            // Navigate to default route based on domain type
            if (isAddEditHost() && session?.user?.tenant) {
              navigate(`/${session.user.tenant}/create`);
            } else {
              navigate('/create');
            }
          }
        } else {
          // No next parameter, go to default route
          if (isAddEditHost() && session?.user?.tenant) {
            navigate(`/${session.user.tenant}/create`);
          } else {
            navigate('/create');
          }
        }
      } catch (error) {
        console.error('Failed to process auth success:', error);
        setIsProcessing(false);
      }
    };

    handleAuthSuccess();
  }, [
    location,
    navigate,
    externalAuthChecks,
    isAddEditHost,
    session,
    tenant,
    initializeTenant,
  ]);

  return (
    <Loader showHeaderFooter={false} text={'Completing authentication...'} />
  );
};

export default AuthSuccess;
